import Moment from 'moment';

const round = (number, decimals) => {
  let decimalsCopy = decimals;
  if (!decimalsCopy) {
    decimalsCopy = 0;
  }

  return Math.round(number * (10 ** decimalsCopy)) / (10 ** decimalsCopy);
};

const formatNumber = (number, minDecimals = 0, maxDecimals = 3, locale = 'de-DE') => (
  parseFloat(number).toLocaleString(locale, {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  })
);

const generateDays = () => {
  const now = new Moment();
  const days = [];

  for (let i = 0; i < 7; i += 1) {
    let startTime = new Moment(now);
    startTime.add(i, 'days');

    if (i !== 0) {
      startTime = startTime.startOf('day');
    }

    const endTime = new Moment(startTime);
    endTime.endOf('day');

    days.push({
      startTime: parseInt(Moment.utc(startTime).format('x'), 10),
      endTime: parseInt(Moment.utc(endTime).format('x'), 10),
      id: (i + 1) * 100,
    });
  }

  return days;
};

const generateHours = () => {
  const now = new Moment();
  const startTime = new Moment(now);
  let endTime;
  let hour = 3;

  const hours = [];

  for (let i = 1; i <= 4; i += 1) {
    endTime = new Moment(startTime);
    if (i > 1) {
      hour *= 2;
    }
    endTime.add(hour, 'hours');

    hours.push({
      startTime: parseInt(Moment.utc(startTime).format('x'), 10),
      endTime: parseInt(Moment.utc(endTime).format('x'), 10),
      id: i,
      title: `${hour}h`,
      timeSpan: hour,
    });
  }

  return hours;
};

// https://stackoverflow.com/a/901144
const getUrlParam = (paramName) => {
  const { href } = document.location;
  const paramNameCopy = paramName.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${paramNameCopy}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(href);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

const groupBets = (mBets, bets) => {
  const matchBets = mBets;
  const groupedBets = [];

  for (let i = 0; i < matchBets.length; i += 1) {
    const bet = bets[matchBets[i].id];
    if (!bet.outcomes || (bet.outcomes && !bet.outcomes.length)) continue;

    if (bet && bet.grouping) {
      matchBets[i].grouping = true;
      const index = _.findIndex(groupedBets, { id: matchBets[i].id });
      if (index === -1) {
        groupedBets.push(matchBets[i]);
      } else {
        // Merge outcomes
        groupedBets[index].grouped = true;
        groupedBets[index].outcomes.push(...matchBets[i].outcomes);
      }
    } else {
      groupedBets.push(matchBets[i]);
    }
  }

  return groupedBets;
};

// Results should be same for params (3, 10) or (10, 3)
const getNumberOfCombs = (num1, num2) => {
  const { lower, higher } = { lower: num1 > num2 ? num2 : num1, higher: num1 > num2 ? num1 : num2 };

  let x = 1;
  let y = 1;

  for (let i = higher; i > (higher - lower); i -= 1) {
    x *= i;
  }

  for (let i = lower; i > 0; i -= 1) {
    y *= i;
  }

  return parseFloat(x) / parseFloat(y);
};

const getSystemCombsOdds = (systemOdds, systemLoweNumber, callback) => {
  const n = systemOdds.length;
  const c = [];
  const inner = (start, choose_) => {
    if (choose_ === 0) {
      callback(c);
    } else {
      for (let i = start; i <= n - choose_; i += 1) {
        c.push(systemOdds[i]);
        inner(i + 1, choose_ - 1);
        c.pop();
      }
    }
  };
  inner(0, systemLoweNumber);
};

const arrayMin = (arr) => {
  let len = arr.length;
  let min = Infinity;
  // eslint-disable-next-line
  while (len--) {
    if (arr[len] < min) {
      min = arr[len];
    }
  }
  return min;
};

// NOTE: toType 1=decimal, 2=fractional, 3=american
const toLocaleOdd = (odd, toType, roundDecimals) => {
  // Set default type
  const toTypeCopy = parseInt(toType, 10) || 1;
  const roundDecimalsCopy = roundDecimals >= 0 ? roundDecimals : 2;

  // Conversion map
  const convertMap = {
    1: 'toDecimal',
    2: 'toFractional',
    3: 'toAmerican',
  };

  // Set start value
  let value = odd;

  // Greatest common divisor
  const greatestDivisor = (a, b) => {
    if (!b) {
      return a;
    }
    return greatestDivisor(b, a % b);
  };

  // Conversion function
  const convert = {
    toDecimal: () => {
      value = round(value, roundDecimalsCopy).toFixed(roundDecimalsCopy);
    },
    toFractional: () => {
      const odds = [
        1.01, 1.02, 1.0303, 1.04, 1.1, 1.125, 1.143, 1.167, 1.2, 1.222, 1.25,
        1.286, 1.3, 1.333, 1.364, 1.4, 1.444, 1.5, 1.533, 1.571, 1.615, 1.667,
        1.727, 1.8, 1.833, 1.909, 2.0, 2.1, 2.2, 2.25, 2.3, 2.375, 2.5, 2.625,
        2.75, 3.0, 3.2, 3.25, 3.4, 3.5, 3.75, 4.0, 4.333, 4.5, 5.0, 5.5, 6.0,
        6.5, 7.0, 7.5, 8.0, 8.5, 9.0, 9.5, 10.0, 11.0, 12.0, 13.0, 15.0, 17.0,
        21.0, 26.0, 34.0, 41.0, 51.0, 67.0, 101.0,
      ];

      const oddsMap = {
        '303/10000': '1/33',
        '143/1000': '1/7',
        '167/1000': '1/6',
        '111/500': '2/9',
        '143/500': '2/7',
        '333/1000': '1/3',
        '91/250': '4/11',
        '111/250': '4/9',
        '533/1000': '8/15',
        '571/1000': '4/7',
        '123/200': '8/13',
        '667/1000': '4/6',
        '727/1000': '8/11',
        '833/1000': '5/6',
        '909/1000': '10/11',
        '3333/1000': '10/3',
      };

      const closest = odds.reduce((a, b) => (Math.abs(b - value) < Math.abs(a - value) ? b : a));

      value = Math.abs(closest - value) < 1 ? closest : value;

      const valueString = value.toString();
      const point = valueString.indexOf('.');
      const decimals = (point > -1) ? valueString.substring(point + 1).length : 1;
      const multiplier = 10 ** decimals;
      const number = round(((value - 1) * multiplier), decimals);
      const divisor = greatestDivisor(number, multiplier);

      value = `${(number / divisor)}/${(multiplier / divisor)}`;
      value = oddsMap[value] ? oddsMap[value] : value;
    },
    toAmerican: () => {
      const multiplier = 100;

      if (value <= 1) {
        value = '+0';
        return;
      }

      if (value >= 2) {
        value = `+${round((value - 1) * multiplier, 0)}`;
      } else {
        value = `-${round(Math.abs(multiplier / (value - 1), 0))}`;
      }
    },
  };

  convert[convertMap[toTypeCopy]]();

  return value.toString();
};

const prepareForPrint = (ticketAction, getters) => {
  if (!ticketAction) {
    return false;
  }

  const oddType = getters.getActiveOddType || 1;
  const result = _.cloneDeep(ticketAction.responseData);


  _.forEach(result.bets, (item) => {
    const tmpItem = item;
    if (tmpItem.oddValue) {
      tmpItem.displayOddValue = toLocaleOdd(tmpItem.oddValue, oddType);
    }

    if (!tmpItem.bet.betGroupingEnabled && tmpItem.specialValue && tmpItem.specialValue !== '*') {
      tmpItem.bet.betDisplayName += ` (${tmpItem.specialValue})`;
      tmpItem.bet.betShortName += ` (${tmpItem.specialValue})`;
    }
  });

  if (result.totalOdd) {
    result.displayTotalOdd = parseFloat(result.totalOdd).toFixed(2).toString();
  }

  if (getters.taxes) result.taxes = getters.taxes;

  return result;
};

const formatHeaders = settings => ({
  Authorization: `Bearer ${settings.token}`,
  'HTTP-X-NAB-BETSHOP-ID': settings.betshopId,
  'HTTP-X-NAB-COMPANY-ID': settings.companyId,
  'HTTP-X-NAB-DEVICE-UUID': settings.deviceUuid,
  'HTTP-X-NAB-DP': 'Terminal',
  'X-NSFT-SEVEN-COMPANY-UUID': settings.companyUuid,
});

export default {
  maxInt: Number.MAX_SAFE_INTEGER,
  generateDays,
  generateHours,
  getUrlParam,
  groupBets,
  getNumberOfCombs,
  getSystemCombsOdds,
  arrayMin,
  formatNumber,
  toLocaleOdd,
  prepareForPrint,
  formatHeaders,
};
